import React, { useState, useEffect, useMemo, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  ClipboardList, Save, Send, CheckCircle2, Lock, Download,
  Upload, AlertCircle, UserPlus, TrendingUp, DoorOpen, PauseCircle,
  Loader2, Calendar, Search, RotateCcw, Trash2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useActiveCompany } from '../hooks/useActiveCompany';
import { EmployeeSelect } from '../components/EmployeeAutocomplete';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const isObjectId = (str) => typeof str === 'string' && /^[0-9a-fA-F]{24}$/.test(str.trim());
const cleanEmpName = (name, code) => {
  if (name && !isObjectId(name)) return name;
  if (code && !isObjectId(code)) return code;
  return 'Unknown Employee';
};
const cleanEmpCode = (code) => {
  if (code && !isObjectId(code)) return code;
  return '';
};

const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// ── OverrideCell ─────────────────────────────────────────────────────────────
// A table cell that supports manual override of a system-derived value.
// Shows the system value as placeholder, highlights when overridden,
// and shows a reset (✕) button to clear the override.
const OverrideCell = ({
  systemValue,        // auto-calculated value from attendance/engine
  overrideValue,      // saved manual override (null = no override)
  onChange,           // (newValue) => void — called when user types
  onClear,            // () => void — called when user resets to system value
  disabled = false,
  step = '0.5',
  min = '0',
  max,
  placeholder,
  accentColor = 'amber', // amber | emerald | indigo | rose | violet | slate
  width = 'w-20',
  testId,
  title,
}) => {
  const isOverridden = overrideValue !== null && overrideValue !== undefined && overrideValue !== '';
  const displayValue = isOverridden ? overrideValue : (systemValue ?? '');
  const systemLabel = systemValue !== null && systemValue !== undefined ? String(systemValue) : '—';

  const accent = {
    amber:  { bg: 'bg-amber-50',   border: 'focus:border-amber-500 hover:border-amber-300', badge: 'bg-amber-100 text-amber-700' },
    emerald:{ bg: 'bg-emerald-50', border: 'focus:border-emerald-500 hover:border-emerald-300', badge: 'bg-emerald-100 text-emerald-700' },
    indigo: { bg: 'bg-indigo-50',  border: 'focus:border-indigo-500 hover:border-indigo-300', badge: 'bg-indigo-100 text-indigo-700' },
    rose:   { bg: 'bg-rose-50',    border: 'focus:border-rose-500 hover:border-rose-300', badge: 'bg-rose-100 text-rose-700' },
    violet: { bg: 'bg-violet-50',  border: 'focus:border-violet-500 hover:border-violet-300', badge: 'bg-violet-100 text-violet-700' },
    slate:  { bg: 'bg-slate-100',  border: 'focus:border-slate-500 hover:border-slate-300', badge: 'bg-slate-200 text-slate-600' },
  }[accentColor] || { bg: 'bg-amber-50', border: 'focus:border-amber-500 hover:border-amber-300', badge: 'bg-amber-100 text-amber-700' };

  return (
    <td className={`text-right px-1 py-1.5 ${isOverridden ? accent.bg : ''}`}>
      <div className="flex flex-col items-end gap-0.5">
        {isOverridden && (
          <div className={`flex items-center gap-1 text-[9px] font-semibold px-1.5 rounded-full ${accent.badge}`}>
            <span>OVR</span>
            {!disabled && (
              <button
                onClick={onClear}
                title={`Clear override and restore system value (${systemLabel})`}
                className="ml-0.5 hover:opacity-70 transition-opacity"
              >
                <RotateCcw className="w-2.5 h-2.5" />
              </button>
            )}
          </div>
        )}
        <input
          type="number"
          step={step}
          min={min}
          {...(max !== undefined ? { max } : {})}
          value={displayValue}
          placeholder={placeholder ?? systemLabel}
          disabled={disabled}
          onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
          data-testid={testId}
          title={title ?? (isOverridden
            ? `Override: ${overrideValue} (system: ${systemLabel}). Click ↺ to restore.`
            : `System value: ${systemLabel}. Edit to override.`
          )}
          className={`${width} h-7 text-xs text-right px-1.5 bg-white border border-transparent ${accent.border} rounded outline-none disabled:bg-slate-50 disabled:cursor-not-allowed font-mono placeholder:text-slate-300`}
        />
        {isOverridden && (
          <span className="text-[8px] text-slate-400 font-mono">sys: {systemLabel}</span>
        )}
      </div>
    </td>
  );
};

// ── NumCell ──────────────────────────────────────────────────────────────────
// Simple editable number cell for values that have no separate system-derived
// counterpart (e.g. OT Amount, Incentive, Reimbursement, etc.)
const NumCell = ({ value, systemValue, onChange, onClear, disabled, accentColor, testId }) => {
  const isOverridden = value !== null && value !== undefined && value !== '' && Number(value) !== 0;
  const accent = {
    rose:   { bg: 'bg-rose-50/60',   border: 'focus:border-rose-500 hover:border-rose-300', badge: 'bg-rose-100 text-rose-700' },
    violet: { bg: 'bg-violet-50/60', border: 'focus:border-violet-500 hover:border-violet-300', badge: 'bg-violet-100 text-violet-700' },
    slate:  { bg: '',                 border: 'focus:border-slate-400 hover:border-slate-200', badge: '' },
  }[accentColor] || { bg: '', border: 'focus:border-slate-400 hover:border-slate-200', badge: '' };

  return (
    <td className={`text-right px-1 py-1.5 ${isOverridden ? accent.bg : ''}`}>
      <input
        type="number" step="0.01"
        value={value ?? ''}
        placeholder="0"
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
        data-testid={testId}
        className={`w-20 h-7 text-xs text-right px-1.5 bg-white border border-transparent ${accent.border} rounded outline-none disabled:bg-slate-50 font-mono placeholder:text-slate-300`}
      />
    </td>
  );
};

/** Dynamic salary head cell. */
const DynHeadCell = ({ value, onChange, disabled, allowNegative = false, headType = 'earning' }) => {
  const isDeduction = headType === 'deduction';
  const isOverridden = value !== null && value !== undefined && Number(value) !== 0;
  return (
    <td className={`text-right px-1 py-1.5 ${isOverridden ? (isDeduction ? 'bg-rose-50/60' : 'bg-violet-50/60') : ''}`}>
      <input
        type="number"
        step="0.01"
        min={allowNegative ? undefined : 0}
        value={value ?? ''}
        placeholder="0"
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
        className={`w-24 h-7 text-xs text-right px-1.5 bg-white border border-transparent rounded outline-none font-mono disabled:bg-slate-50 placeholder:text-slate-300 ${
          isDeduction ? 'hover:border-rose-300 focus:border-rose-500' : 'hover:border-violet-300 focus:border-violet-500'
        }`}
      />
    </td>
  );
};

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className={`bg-${color}-50 border border-${color}-200 rounded-xl p-3`}>
    <div className="flex items-center justify-between">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <Icon className={`w-4 h-4 text-${color}-500`} />
    </div>
    <p className="text-2xl font-semibold text-slate-900 mt-1 font-mono">{value ?? '—'}</p>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
const MonthlyInput = () => {
  const { user } = useAuth();
  const canEdit = ['tenant_admin', 'hr_admin', 'payroll_admin'].some(r =>
    (user?.roles || [user?.role]).includes(r)
  );

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [period, setPeriod] = useState(currentPeriod());
  const [grid, setGrid] = useState(null);
  const [dynHeadDefs, setDynHeadDefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [dirty, setDirty] = useState({});
  const [activeTab, setActiveTab] = useState('system');
  const [systemDerived, setSystemDerived] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/companies`, { withCredentials: true })
      .then(({ data }) => {
        setCompanies(data);
        setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
      }).catch(() => {});
  }, [setSelectedCompany]);

  const fetchGrid = useCallback(async () => {
    if (!selectedCompany || !period) return;
const DERIVED_OR_REFERENCE_CODES = new Set([
  'GROSS_MONTHLY', 'GROSS', 'GROSS_PAY', 'EARNED_GROSS', 'FIXED_GROSS', 'TOTAL_GROSS',
  'CTC_MONTHLY', 'CTC', 'MONTHLY_CTC', 'CTC_REF', 'CTC_REFERENCE', 'CTC_ANNUAL',
  'NET_PAY', 'NET', 'NET_SALARY', 'TOTAL_DEDUCTIONS', 'TOTAL_EMPLOYER_CONTRIBUTIONS', 'EMPLOYER_COST'
]);

const NON_MONETARY_CODES = new Set([
  'ATT_OT_HOURS', 'OT_HOURS', 'SYSTEM_ATT_OT_HOURS',
  'PRESENT_DAYS', 'PRESENT_DAYS_OVERRIDE', 'SYSTEM_PRESENT_DAYS',
  'SYSTEM_LOP_DAYS', 'LOP_DAYS', 'LOP_DAYS_OVERRIDE',
  'LEAVE_DAYS', 'HOLIDAYS', 'WEEKLY_OFF', 'WEEKLY_OFFS',
  'TOTAL_DAYS', 'MONTH_DAYS', 'BASE_DAYS', 'BASE_DAYS_OVERRIDE',
  'ADDITIONAL_DAYS', 'PAYABLE_DAYS', 'PAID_DAYS', 'DAYS',
  'HAS_INCREMENT', 'IS_NEW_JOINEE', 'IS_RESIGNEE', 'IS_UNDER_NOTICE',
]);

const isNonMonetaryField = (hd) => {
  if (!hd) return false;
  let code = '';
  if (typeof hd === 'string') code = hd.trim().toUpperCase();
  else code = String(hd.code || hd.head_code || hd.id || '').trim().toUpperCase();
  if (code.startsWith('EARNED_')) code = code.slice(7);
  else if (code.startsWith('FIXED_')) code = code.slice(6);
  else if (code.startsWith('DED_')) code = code.slice(4);

  if (NON_MONETARY_CODES.has(code)) return true;
  if (code.endsWith('_DAYS') || code.endsWith('_HOURS') || code.endsWith('_DAY') || code.endsWith('_HOUR')) return true;
  if (code.startsWith('SYSTEM_') || code.startsWith('HAS_') || code.startsWith('IS_')) return true;
  return false;
};

const isDerivedOrReferenceHead = (hd) => {
  if (!hd) return false;
  if (isNonMonetaryField(hd)) return true;
  if (typeof hd === 'string') return DERIVED_OR_REFERENCE_CODES.has(hd.trim().toUpperCase());
  const code = String(hd.code || hd.head_code || '').trim().toUpperCase();
  const headType = String(hd.head_type || hd.type || hd.category || '').trim().toLowerCase();
  if (['derived', 'ctc_reference', 'reference', 'summary', 'ctc_monthly'].includes(headType)) return true;
  if (hd.is_derived === true || hd.isDerived === true) return true;
  if (hd.is_earning === false || hd.isEarning === false) {
    if (!['deduction', 'employer_contribution'].includes(headType)) return true;
  }
  if (hd.is_part_of_gross === false || hd.includeInGross === false || hd.include_in_gross === false) {
    if (!['deduction', 'employer_contribution'].includes(headType)) return true;
  }
  return DERIVED_OR_REFERENCE_CODES.has(code);
};

    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/controls/monthly-inputs/grid?company_id=${selectedCompany}&period=${period}`,
        { withCredentials: true }
      );
      setGrid(data);
      setDynHeadDefs((data.dynamic_head_defs || []).filter(hd => !isDerivedOrReferenceHead(hd)));
      setDirty({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load grid');
    }
    setLoading(false);
  }, [selectedCompany, period]);


  useEffect(() => { fetchGrid(); }, [fetchGrid]);

  const fetchSystemDerived = useCallback(async () => {
    if (!selectedCompany || !period) return;
    try {
      const { data } = await axios.get(
        `${API_URL}/controls/monthly-inputs/system-derived?company_id=${selectedCompany}&period=${period}`,
        { withCredentials: true },
      );
      setSystemDerived(data);
    } catch {
      setSystemDerived({ rows: [], counts: {}, pending_lifecycle_events: [] });
    }
  }, [selectedCompany, period]);

  useEffect(() => { fetchSystemDerived(); }, [fetchSystemDerived]);

  // Update a field — supports nested dynamic_heads.HEADCODE notation
  const onChange = (eid, field, value) => {
    setDirty(prev => {
      const base = prev[eid] || grid.rows.find(r => r.employee_id === eid);
      if (field.startsWith('dynamic_heads.')) {
        const headCode = field.slice('dynamic_heads.'.length);
        return {
          ...prev,
          [eid]: {
            ...base,
            dynamic_heads: { ...(base.dynamic_heads || {}), [headCode]: value },
          },
        };
      }
      return { ...prev, [eid]: { ...base, [field]: value } };
    });
  };

  // Clear (reset) an override field back to null so the system value is used
  const onClear = (eid, field) => onChange(eid, field, null);

  const mergedRows = useMemo(() => {
    if (!grid) return [];
    return grid.rows.map(r => (dirty[r.employee_id] ? { ...r, ...dirty[r.employee_id] } : r));
  }, [grid, dirty]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return mergedRows;
    return mergedRows.filter(r =>
      r.employee_code?.toLowerCase().includes(q) ||
      r.employee_name?.toLowerCase().includes(q) ||
      r.department?.toLowerCase().includes(q)
    );
  }, [mergedRows, query]);

  const handleSave = async () => {
    const parseVal = (v) => (v === '' || v == null ? null : Number(v));

    const rowsToSave = Object.values(dirty).map(r => {
      const dynObj = {};
      if (r.dynamic_heads) {
        Object.entries(r.dynamic_heads).forEach(([k, v]) => {
          dynObj[k] = parseVal(v);
        });
      }
      return {
        ...r,
        employee_id: r.employee_id,
        present_days_override: parseVal(r.present_days_override),
        ot_hours: parseVal(r.ot_hours),
        ot_amount: parseVal(r.ot_amount),
        incentive: parseVal(r.incentive),
        performance_incentive: parseVal(r.performance_incentive),
        bonus: parseVal(r.bonus),
        variable_earning: parseVal(r.variable_earning),
        arrears: parseVal(r.arrears),
        basic_arrear: parseVal(r.basic_arrear),
        hra_arrear: parseVal(r.hra_arrear),
        conveyance_arrear: parseVal(r.conveyance_arrear),
        relocation_arrears: parseVal(r.relocation_arrears),
        medical_arrear: parseVal(r.medical_arrear),
        special_allowance_arrear: parseVal(r.special_allowance_arrear),
        other_allowance: parseVal(r.other_allowance),
        reimbursement: parseVal(r.reimbursement),
        other_earning: parseVal(r.other_earning),
        other_deduction: parseVal(r.other_deduction),
        pt_arrear: parseVal(r.pt_arrear),
        esi_arrear: parseVal(r.esi_arrear),
        pf_arrear: parseVal(r.pf_arrear),
        lwf: parseVal(r.lwf),
        salary_advance_recovery: parseVal(r.salary_advance_recovery),
        advance_recovery: parseVal(r.advance_recovery),
        notice_pay_recovery: parseVal(r.notice_pay_recovery),
        hold_salary: !!r.hold_salary,
        remarks: r.remarks || '',
        reason: r.reason || r.remarks || '',
        lop_days_override: parseVal(r.lop_days_override),
        additional_days: parseVal(r.additional_days),
        base_days_override: parseVal(r.base_days_override),
        tds_override: parseVal(r.tds_override),
        dynamic_heads: Object.keys(dynObj).length > 0 ? dynObj : undefined,
      };
    });
    if (rowsToSave.length === 0) { toast.info('No changes to save'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/controls/monthly-inputs/grid`,
        { company_id: selectedCompany, period, rows: rowsToSave },
        { withCredentials: true });
      toast.success(`Saved ${rowsToSave.length} rows`);
      fetchGrid();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const handleAction = async (action) => {
    if (!window.confirm(`${action.toUpperCase()} all monthly inputs for ${period}?`)) return;
    try {
      await axios.post(
        `${API_URL}/controls/monthly-inputs/action?company_id=${selectedCompany}&period=${period}`,
        { action, remarks: '' }, { withCredentials: true });
      toast.success(`Action '${action}' completed`);
      fetchGrid();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const handleDeleteMonthData = async () => {
    if (!selectedCompany) {
      toast.error('Please select a company.');
      return;
    }
    if (!period) {
      toast.error('Please select a payroll period.');
      return;
    }
    setDeleting(true);
    try {
      const { data } = await axios.delete(
        `${API_URL}/controls/monthly-inputs/period?company_id=${encodeURIComponent(selectedCompany)}&period=${encodeURIComponent(period)}`,
        { withCredentials: true }
      );
      toast.success(data.message || `Deleted ${data.deleted_count} rows successfully`);
      setDirty({});
      setShowDeleteModal(false);
      fetchGrid();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete month data');
    }
    setDeleting(false);
  };

  const downloadTemplate = () => {
    window.open(`${API_URL}/controls/monthly-inputs/template/download?company_id=${selectedCompany}&period=${period}`, '_blank');
  };

  const onCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(
        `${API_URL}/controls/monthly-inputs/template/upload?company_id=${selectedCompany}&period=${period}`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (data.success_count > 0) { toast.success(`Uploaded ${data.success_count} rows successfully`); fetchGrid(); }
      if (data.error_count > 0) { toast.error(`${data.error_count} rows had errors`); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    e.target.value = '';
  };

  const inputStatus = grid?.input_status || 'no_data';
  const statusColor = {
    no_data: 'bg-slate-100 text-slate-600',
    draft: 'bg-amber-100 text-amber-700',
    pending_approval: 'bg-blue-100 text-blue-700',
    approved: 'bg-green-100 text-green-700',
    locked: 'bg-purple-100 text-purple-700',
    rejected: 'bg-red-100 text-red-700',
  }[inputStatus] || 'bg-slate-100';

  return (
    <MainLayout>
      <div className="space-y-5" data-testid="monthly-input-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
              <ClipboardList className="w-7 h-7 text-[#0052FF]" /> Monthly Inputs
            </h1>
            <p className="mt-1 text-sm text-slate-600 max-w-3xl">
              Variable inputs for each payroll month — OT, incentives, reimbursement, other deductions, and hold-salary toggle.
              Attendance + LOP auto-flow from daily attendance. Fixed salary components auto-flow from approved salary masters.{' '}
              <strong>Any column can be manually overridden.</strong> Overridden cells show an <span className="text-amber-700 font-semibold">OVR</span> badge and display the original system value below.
            </p>
            <p className="mt-1.5 text-xs text-violet-700 max-w-3xl" data-testid="mi-dynamic-heads-hint">
              <strong>Need a custom MI column?</strong> Go to{' '}
              <a href="/masters?master=salary_heads" className="underline font-semibold hover:text-violet-900">
                Masters → Salary Heads
              </a>{' '}
              and toggle <b>"Monthly Input" = ON</b>. It auto-appears as a column in the CSV template and gets processed by the engine next run — no code change required.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} data-testid="mi-company-select"
              className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm">
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="flex items-center gap-1 h-10 px-3 bg-white border border-slate-300 rounded-md">
              <Calendar className="w-4 h-4 text-slate-400" />
              <input type="month" value={period} onChange={e => setPeriod(e.target.value)} data-testid="mi-period-input"
                className="outline-none text-sm bg-transparent" />
            </div>
            <span className={`text-xs font-semibold uppercase px-2.5 py-1 rounded-full ${statusColor}`} data-testid="mi-status">
              {inputStatus.replace(/_/g, ' ')}
            </span>
          </div>
        </div>

        {/* Flag summary */}
        {grid && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="mi-flag-summary">
            <Stat icon={UserPlus} label="Total Employees" value={grid.total_employees} color="blue" />
            <Stat icon={UserPlus} label="New Joiners" value={grid.joinee_count} color="green" />
            <Stat icon={TrendingUp} label="Increments" value={grid.increment_count} color="violet" />
            <Stat icon={DoorOpen} label="Resigning" value={grid.resignee_count} color="red" />
            <Stat icon={PauseCircle} label="On Hold" value={grid.hold_count} color="amber" />
          </div>
        )}

        {/* Tab toggle */}
        <div className="flex bg-slate-100 rounded-lg p-1 w-fit" data-testid="mi-tab-toggle">
          <button
            data-testid="mi-tab-system"
            onClick={() => setActiveTab('system')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'system' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            System-derived (read-only)
            {systemDerived?.counts?.total > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold">
                {systemDerived.counts.total}
              </span>
            )}
          </button>
          <button
            data-testid="mi-tab-manual"
            onClick={() => setActiveTab('manual')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'manual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            Manual overrides
            {Object.keys(dirty).length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
                {Object.keys(dirty).length} unsaved
              </span>
            )}
          </button>
        </div>

        {/* SYSTEM-DERIVED TAB */}
        {activeTab === 'system' && (
          <div className="space-y-3" data-testid="mi-system-view">
            {systemDerived?.guidance && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-900">
                {systemDerived.guidance}
              </div>
            )}
            {systemDerived?.counts && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center text-xs" data-testid="mi-system-counts">
                <div className="bg-white border border-slate-200 rounded-lg p-3"><p className="text-xl font-semibold text-slate-900">{systemDerived.counts.total || 0}</p><p className="text-slate-500">Tracked</p></div>
                <div className="bg-white border border-slate-200 rounded-lg p-3"><p className="text-xl font-semibold text-emerald-600">{systemDerived.counts.new_joiners_this_period || 0}</p><p className="text-slate-500">New joiners</p></div>
                <div className="bg-white border border-slate-200 rounded-lg p-3"><p className="text-xl font-semibold text-red-600">{systemDerived.counts.resignations_this_period || 0}</p><p className="text-slate-500">Resigning</p></div>
                <div className="bg-white border border-slate-200 rounded-lg p-3"><p className="text-xl font-semibold text-amber-600">{systemDerived.counts.on_payment_hold || 0}</p><p className="text-slate-500">Payment hold</p></div>
                <div className="bg-white border border-slate-200 rounded-lg p-3"><p className="text-xl font-semibold text-slate-800">{systemDerived.counts.on_payroll_hold || 0}</p><p className="text-slate-500">Payroll hold (skipped)</p></div>
              </div>
            )}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600"><tr>
                  <th className="text-left py-2 px-3 font-medium">Employee</th>
                  <th className="text-left py-2 px-3 font-medium">Status</th>
                  <th className="text-left py-2 px-3 font-medium">Payroll</th>
                  <th className="text-left py-2 px-3 font-medium">DOJ / LWD</th>
                  <th className="text-center py-2 px-3 font-medium">LOP</th>
                  <th className="text-center py-2 px-3 font-medium">Approved Leaves</th>
                  <th className="text-left py-2 px-3 font-medium">Sources</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {(systemDerived?.rows || []).map((r) => (
                    <tr key={r.employee_id} data-testid={`mi-sys-row-${r.employee_id}`} className="hover:bg-slate-50">
                      <td className="py-2 px-3"><p className="font-medium text-slate-800">{cleanEmpName(r.employee_name, r.employee_code)}</p>{cleanEmpCode(r.employee_code) && <p className="text-xs text-slate-500 font-mono">{cleanEmpCode(r.employee_code)}</p>}</td>
                      <td className="py-2 px-3 capitalize">{r.employment_status}</td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          r.payroll_status === 'payroll_hold' ? 'bg-slate-200 text-slate-700'
                          : r.payroll_status === 'payment_hold' ? 'bg-amber-100 text-amber-800'
                          : 'bg-emerald-100 text-emerald-800'
                        }`}>{r.payroll_status.replace('_', ' ')}</span>
                      </td>
                      <td className="py-2 px-3 text-xs">
                        {r.date_of_joining && <p>DOJ {r.date_of_joining}</p>}
                        {r.last_working_day && <p className="text-red-600">LWD {r.last_working_day}</p>}
                      </td>
                      <td className="py-2 px-3 text-center">{r.lop_days ?? '—'}</td>
                      <td className="py-2 px-3 text-center">{r.approved_leaves_in_period}</td>
                      <td className="py-2 px-3">
                        <div className="flex flex-wrap gap-1">
                          {r.sources.map((s) => (
                            <span key={s} className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                              s === 'payroll_hold' ? 'bg-slate-200 text-slate-700'
                              : s === 'payment_hold' ? 'bg-amber-100 text-amber-800'
                              : s === 'new_joiner' ? 'bg-emerald-100 text-emerald-800'
                              : s === 'resignation' ? 'bg-red-100 text-red-700'
                              : s === 'ctc' ? 'bg-blue-50 text-blue-700'
                              : 'bg-slate-100 text-slate-600'
                            }`}>{s.replace('_', ' ')}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(!systemDerived?.rows || systemDerived.rows.length === 0) && (
                    <tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">No employees under this company.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* MANUAL OVERRIDES TAB */}
        {activeTab === 'manual' && (
          <>
          {/* Info banners */}
          <div className="bg-violet-50 border border-violet-200 rounded-md px-3 py-2 text-[11px] text-violet-900 flex items-start gap-2" data-testid="mi-source-banner">
            <span className="shrink-0">💡</span>
            <div>
              <b>Single Source of Truth:</b> The data shown here is the same store used by{' '}
              <b>Business Controls → Monthly Inputs</b>. Any values saved or uploaded from Business Controls
              automatically appear here — and any edits you make directly here are also reflected there.
              No manual sync is ever needed.
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-[11px] text-amber-900 flex items-start gap-2" data-testid="mi-override-banner">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              <b>All columns are overrideable.</b> Cells showing an <span className="font-bold text-amber-700">OVR</span> badge have been manually overridden. The system-derived value is shown below the input for reference. Click the <RotateCcw className="inline w-3 h-3 mx-0.5" /> icon to clear the override and return to the auto-calculated value. Overridden values are used by the payroll engine on every run and re-run until cleared.
            </div>
          </div>

          {/* Toolbar */}
          {canEdit && (
            <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="mi-toolbar">
              <div className="w-72">
                <EmployeeSelect
                  companyId={selectedCompany}
                  value=""
                  onChange={(empId, empDoc) => {
                    if (empDoc) setQuery(empDoc.employee_code || empDoc.full_name || '');
                  }}
                  placeholder="Quick search employee…"
                  data-testid="mi-search-select"
                />
              </div>
              <div className="relative flex-1 min-w-[240px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  placeholder="Search by code, name, department"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  data-testid="mi-search"
                  className="w-full h-9 pl-10 pr-3 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:bg-white"
                />
              </div>
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="mi-download-template">
                <Download className="w-4 h-4 mr-1.5" /> Template
              </Button>
              <label className="inline-flex items-center gap-1.5 h-9 px-3 bg-white border border-slate-200 rounded-md text-sm font-medium cursor-pointer hover:bg-slate-50">
                <Upload className="w-4 h-4" /> Bulk CSV
                <input type="file" accept=".csv" hidden onChange={onCsvUpload} data-testid="mi-csv-upload" />
              </label>
              <Button onClick={handleSave} disabled={saving || Object.keys(dirty).length === 0}
                data-testid="mi-save" className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Save {Object.keys(dirty).length ? `(${Object.keys(dirty).length})` : ''}
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleAction('submit')} data-testid="mi-submit">
                <Send className="w-4 h-4 mr-1.5" /> Submit
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleAction('approve')} data-testid="mi-approve">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleAction('lock')} data-testid="mi-lock">
                <Lock className="w-4 h-4 mr-1.5" /> Lock
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteModal(true)}
                disabled={!canEdit || ['locked'].includes(inputStatus)}
                data-testid="mi-delete-month-btn"
                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                title="Delete all saved monthly input records for the selected company and period"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete Month Data
              </Button>
            </div>
          )}

          {/* Grid */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="mi-grid">
            {loading ? (
              <div className="py-20 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : !grid || filteredRows.length === 0 ? (
              <div className="py-20 text-center text-slate-500">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                No employees found for this period.
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-2 sticky left-0 bg-slate-50 z-10 min-w-[220px]">Employee</th>
                    <th className="text-center px-2 py-2">Flag</th>
                    {/* Attendance override columns */}
                    <th className="text-right px-2 py-2 bg-sky-50 text-sky-800" title="Present Days — editable override. System value auto-flows from attendance.">Present Days</th>
                    <th className="text-right px-2 py-2 bg-red-50 text-red-800" title="LOP Days — auto-calculated from attendance.">LOP (sys)</th>
                    <th className="text-right px-2 py-2 bg-amber-50 text-amber-800" title="LOP Override — manually override the attendance-derived LOP.">LOP Override</th>
                    <th className="text-right px-2 py-2 bg-emerald-50 text-emerald-800" title="Additional Days on top of pay cycle (Sunday work / festive bonus).">Addl. Days</th>
                    <th className="text-right px-2 py-2 bg-indigo-50 text-indigo-800" title="Base Days — override the proration denominator for this employee/month.">Base Days</th>
                    {/* Earnings override columns */}
                    <th className="text-right px-2 py-2" title="OT Hours">OT Hrs</th>
                    <th className="text-right px-2 py-2" title="OT Amount">OT Amt</th>
                    <th className="text-right px-2 py-2" title="Incentive">Incentive</th>
                    <th className="text-right px-2 py-2" title="Arrears">Arrears</th>
                    <th className="text-right px-2 py-2" title="Reimbursement">Reimb.</th>
                    <th className="text-right px-2 py-2" title="Other Earnings (+)">Other +</th>
                    {/* Deductions override columns */}
                    <th className="text-right px-2 py-2" title="Other Deductions (-)">Other −</th>
                    {/* Dynamic salary head columns */}
                    {dynHeadDefs.map(hd => (
                      <th
                        key={hd.code}
                        className={`text-right px-2 py-2 min-w-[100px] ${
                          hd.head_type === 'deduction' ? 'bg-rose-50 text-rose-800' : 'bg-violet-50 text-violet-800'
                        }`}
                        title={`${hd.name} — dynamic salary head (${hd.head_type})`}
                      >{hd.name}</th>
                    ))}
                    {/* TDS columns */}
                    <th className="text-right px-2 py-2 bg-slate-100 text-slate-700 min-w-[100px]" title="Calculated TDS from Payroll Run.">Calc. TDS</th>
                    <th className="text-right px-2 py-2 bg-rose-50 text-rose-800 min-w-[100px]" title="TDS Override — replaces deduction for this month only.">TDS Override</th>
                    <th className="text-center px-2 py-2">Hold</th>
                    <th className="text-left px-2 py-2 min-w-[140px]">Remarks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredRows.map(r => {
                    const locked = ['approved', 'locked'].includes(inputStatus);
                    const dis = !canEdit || locked;
                    return (
                      <tr key={r.employee_id} data-testid={`mi-row-${r.employee_code}`} className={`hover:bg-slate-50 ${r.hold_salary ? 'bg-amber-50/40' : ''}`}>
                        {/* Employee info */}
                        <td className="px-3 py-2 sticky left-0 bg-white z-10">
                          <div className="font-medium text-slate-900">{cleanEmpName(r.employee_name, r.employee_code)}</div>
                          <div className="text-[10px] text-slate-500 flex items-center gap-2">
                            {cleanEmpCode(r.employee_code) && <span className="font-mono">{cleanEmpCode(r.employee_code)}</span>}
                            {r.department && <span>· {r.department}</span>}
                          </div>
                        </td>
                        {/* Flags */}
                        <td className="text-center px-2 py-2">
                          <div className="flex flex-col gap-0.5 items-center">
                            {r.is_new_joinee && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700" title="New Joiner">NEW</span>}
                            {r.has_increment && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700" title="Increment">INC</span>}
                            {r.is_resignee && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700" title="Resigning">EXIT</span>}
                          </div>
                        </td>

                        {/* Present Days override */}
                        <OverrideCell
                          systemValue={r.system_present_days}
                          overrideValue={r.present_days_override}
                          onChange={v => onChange(r.employee_id, 'present_days_override', v)}
                          onClear={() => onClear(r.employee_id, 'present_days_override')}
                          disabled={dis}
                          step="0.5"
                          min="0"
                          accentColor="amber"
                          testId={`mi-present-days-${r.employee_code}`}
                          title="Override present days for this employee this month. Affects LOP derivation and pay proration."
                        />

                        {/* LOP system (read-only display) */}
                        <td className="text-right px-2 py-2 text-red-600 font-medium font-mono">{r.system_lop_days ?? r.lop_days ?? '—'}</td>

                        {/* LOP Override */}
                        <OverrideCell
                          systemValue={r.system_lop_days}
                          overrideValue={r.lop_days_override}
                          onChange={v => onChange(r.employee_id, 'lop_days_override', v === '' ? null : v)}
                          onClear={() => onClear(r.employee_id, 'lop_days_override')}
                          disabled={dis}
                          step="0.5"
                          min="0"
                          accentColor="amber"
                          testId={`mi-lop-override-${r.employee_code}`}
                          title="Manually override LOP days. Blank = engine uses attendance auto-calc."
                        />

                        {/* Additional Days */}
                        <OverrideCell
                          systemValue={0}
                          overrideValue={r.additional_days}
                          onChange={v => onChange(r.employee_id, 'additional_days', v === '' ? null : v)}
                          onClear={() => onClear(r.employee_id, 'additional_days')}
                          disabled={dis}
                          step="0.5"
                          min="0"
                          accentColor="emerald"
                          testId={`mi-additional-days-${r.employee_code}`}
                          title="Additional days released on top of pay cycle (Sunday work, festive bonus, etc.). Earnings prorate UP by this many days."
                        />

                        {/* Base Days Override */}
                        <OverrideCell
                          systemValue={null}
                          overrideValue={r.base_days_override}
                          onChange={v => onChange(r.employee_id, 'base_days_override', v === '' ? null : v)}
                          onClear={() => onClear(r.employee_id, 'base_days_override')}
                          disabled={dis}
                          step="0.5"
                          min="0"
                          max="31"
                          accentColor="indigo"
                          testId={`mi-base-days-${r.employee_code}`}
                          title="Override base days denominator for proration this month. Blank = engine uses Wage Master / group default."
                        />

                        {/* OT Hours */}
                        <OverrideCell
                          systemValue={r.system_att_ot_hours ?? r.att_ot_hours}
                          overrideValue={r.ot_hours !== (r.system_att_ot_hours ?? r.att_ot_hours) && r.ot_hours != null ? r.ot_hours : null}
                          onChange={v => onChange(r.employee_id, 'ot_hours', v)}
                          onClear={() => onClear(r.employee_id, 'ot_hours')}
                          disabled={dis}
                          step="0.5"
                          min="0"
                          accentColor="slate"
                          testId={`mi-ot-hours-${r.employee_code}`}
                          title="OT Hours this month. Auto-populated from attendance OT records."
                        />

                        {/* OT Amount */}
                        <NumCell value={r.ot_amount} onChange={v => onChange(r.employee_id, 'ot_amount', v)} disabled={dis} />

                        {/* Incentive */}
                        <NumCell value={r.incentive} onChange={v => onChange(r.employee_id, 'incentive', v)} disabled={dis} />

                        {/* Arrears */}
                        <NumCell value={r.arrears} onChange={v => onChange(r.employee_id, 'arrears', v)} disabled={dis} />

                        {/* Reimbursement */}
                        <NumCell value={r.reimbursement} onChange={v => onChange(r.employee_id, 'reimbursement', v)} disabled={dis} />

                        {/* Other Earning */}
                        <NumCell value={r.other_earning} onChange={v => onChange(r.employee_id, 'other_earning', v)} disabled={dis} />

                        {/* Other Deduction */}
                        <NumCell value={r.other_deduction} onChange={v => onChange(r.employee_id, 'other_deduction', v)} disabled={dis} accentColor="rose" />

                        {/* Dynamic salary head value cells */}
                        {dynHeadDefs.map(hd => (
                          <DynHeadCell
                            key={hd.code}
                            value={(r.dynamic_heads || {})[hd.code] ?? 0}
                            allowNegative={hd.allow_negative}
                            headType={hd.head_type}
                            onChange={v => onChange(r.employee_id, `dynamic_heads.${hd.code}`, v)}
                            disabled={dis}
                          />
                        ))}

                        {/* Calculated TDS (read-only) */}
                        <td className="text-right px-2 py-2 bg-slate-100 font-mono text-slate-700 font-medium">
                          {r.calculated_tds != null ? `₹${Number(r.calculated_tds).toLocaleString('en-IN')}` : '—'}
                        </td>

                        {/* TDS Override */}
                        <OverrideCell
                          systemValue={r.calculated_tds}
                          overrideValue={r.tds_override}
                          onChange={v => onChange(r.employee_id, 'tds_override', v === '' ? null : Math.max(0, Number(v)))}
                          onClear={() => onClear(r.employee_id, 'tds_override')}
                          disabled={!canEdit || inputStatus === 'locked'}
                          step="1"
                          min="0"
                          accentColor="rose"
                          width="w-24"
                          testId={`mi-tds-override-${r.employee_code}`}
                          title="Override TDS deduction for this month only. Blank = engine uses calculated TDS."
                        />

                        {/* Hold salary */}
                        <td className="text-center px-2 py-2">
                          <input type="checkbox" checked={!!r.hold_salary} disabled={dis}
                            onChange={e => onChange(r.employee_id, 'hold_salary', e.target.checked)}
                            data-testid={`mi-hold-${r.employee_code}`}
                            className="w-4 h-4 accent-[#0052FF]" />
                        </td>

                        {/* Remarks */}
                        <td className="px-2 py-2">
                          <input type="text" value={r.remarks || ''} disabled={dis}
                            onChange={e => onChange(r.employee_id, 'remarks', e.target.value)}
                            data-testid={`mi-remarks-${r.employee_code}`}
                            className="w-full h-7 px-2 text-xs bg-white border border-transparent focus:border-slate-300 rounded outline-none disabled:bg-slate-50" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          </>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="mi-delete-modal">
            <div className="bg-white rounded-xl shadow-xl border border-slate-200 max-w-md w-full p-6 space-y-4">
              <div className="flex items-center gap-3 text-red-600">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Delete Monthly Input Data?</h3>
                  <p className="text-xs text-slate-500">This action cannot be undone.</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 text-xs space-y-1.5 text-slate-700">
                <div><strong>Company:</strong> {companies.find(c => c.id === selectedCompany)?.name || selectedCompany}</div>
                <div><strong>Payroll Period:</strong> {period}</div>
                <div className="text-red-700 pt-1">
                  This will permanently delete all saved Monthly Input values for this company and payroll period from the database.
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setShowDeleteModal(false)} disabled={deleting} data-testid="mi-delete-cancel">
                  Cancel
                </Button>
                <Button size="sm" onClick={handleDeleteMonthData} disabled={deleting} data-testid="mi-delete-confirm" className="bg-red-600 hover:bg-red-700 text-white">
                  {deleting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
                  Delete Month Data
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default MonthlyInput;
